import React from 'react';

import { getAllStaffInEvents, checkForStaffWarnings } from '@go-shared/lib/grid';
import { ReportsStaffAllocation } from '@go-shared/types/staffAllocation';
import { Time } from '@go-shared/types/time';
import { Event } from '@go-shared/types/event';
import { HourFormat } from '@go-shared/types/brand';
import { VenueWarnings } from '../VenueWarnings/VenueWarnings';
import { StaffGroup } from './StaffGroup/StaffGroup';
import { ReportsGridHeader } from './ReportsGridHeader/ReportsGridHeader';
import { BareSailingDay } from '@go-shared/types/sailing';

import './ReportsGrid.scss';
import { timeToMinutes } from '@go-shared/lib/time';

interface ReportsGridProps {
  events: Event[];
  shipStartTime: Time;
  hourFormat: HourFormat;
  sailingDay: BareSailingDay;
}

const COLUMNS_MAP = [
  'Department',
  'Staff Title',
  'Event',
  'Venue',
  'Event Time',
  'Involvement',
  'Function',
  'Length',
  'Total',
];

export const ReportsGrid = ({
  events,
  shipStartTime,
  hourFormat,
  sailingDay,
}: ReportsGridProps) => {
  const data = buildGridReadyData(events);
  const staffWarnings = checkForStaffWarnings(events, timeToMinutes(shipStartTime));
  return (
    <>
      <div className="aligned-with-logo">
        <ReportsGridHeader sailingDay={sailingDay} />
        <div className="reports-title items-center flex w-full pr-5 pl-5 mb-2">
          <h1 className={'text-grey-dark'}>Staff Report</h1>
          <span className="relative ml-5">
            <VenueWarnings warnings={staffWarnings} />
          </span>
        </div>
      </div>
      <div className={'ReportsGrid w-full inline-flex flex-col flex-nowrap'}>
        <div className="overflow-y-scroll w-full scroll-table">
          <div
            className="reports-header sticky inset-y-0 w-full bg-grey-dark
                      py-3 pl-5 pr-5 inline-flex justify-between"
          >
            {COLUMNS_MAP.map((c, i) => {
              return (
                <div className={`w-1/6 flex items-center col-${c.toLowerCase()}`} key={i}>
                  <div>
                    <h3 className="text-white">{c}</h3>
                  </div>
                </div>
              );
            })}
          </div>
          {data.map((s: ReportsStaffAllocation[], i) => {
            return (
              <div className="bordered-staff-group">
                <StaffGroup
                  key={i}
                  index={i}
                  staffGroup={s}
                  shipStartTime={shipStartTime}
                  hourFormat={hourFormat}
                  staffWarnings={staffWarnings}
                />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

const buildGridReadyData = (events: Event[]): ReportsStaffAllocation[][] => {
  const staff = getAllStaffInEvents(events);
  const staffMap: { [key: string]: { staff: ReportsStaffAllocation[] } } = {};
  const sorted = staff.sort((s1, s2) => {
    const staff1 = s1.template;
    const staff2 = s2.template;

    const compareDepartment = staff1.department.name.localeCompare(staff2.department.name);
    if (compareDepartment === 0) {
      return staff1.title.localeCompare(staff2.title);
    }
    return compareDepartment;
  });

  sorted.forEach(sortedStaff => {
    if (staffMap[sortedStaff.template.title]) {
      staffMap[sortedStaff.template.title].staff.push(sortedStaff);
    } else staffMap[sortedStaff.template.title] = { staff: [sortedStaff] };
  });

  const staffGroups: ReportsStaffAllocation[][] = [];
  Object.keys(staffMap).forEach(key => staffGroups.push(staffMap[key].staff));

  return staffGroups;
};
